// ReportBuilderState class adapted from frontend/src/pages/Analysis/AnalysisScreen.page.tsx
// This class manages the state of available dimensions and measures based on selected items

import type { 
  ReportBuilderDimension, 
  ReportBuilderMeasure 
} from '../services/reportBuilder.service';

// Simple graph structure for managing dependencies
interface GraphNode {
  id: string;
  label: string;
}

interface GraphEdge {
  from: string;
  to: string;
  relationshipId?: string;
}

class SimpleGraph {
  private nodes: Map<string, GraphNode> = new Map();
  private edges: Map<string, GraphEdge[]> = new Map();

  setNode(id: string, label: string) {
    this.nodes.set(id, { id, label });
  }

  setEdge(from: string, to: string, relationshipId?: string) {
    if (!this.edges.has(from)) {
      this.edges.set(from, []);
    }
    this.edges.get(from)!.push({ from, to, relationshipId });
  }

  predecessors(nodeId: string): string[] {
    const preds: string[] = [];
    for (const [from, edges] of this.edges.entries()) {
      for (const edge of edges) {
        if (edge.to === nodeId) {
          preds.push(from);
        }
      }
    }
    return preds;
  }
}

const getAllUpstreamNodes = (nodeId: string, graph: SimpleGraph): string[] => {
  const rv: string[] = [];
  const stack: string[] = [nodeId];
  const visited = new Set<string>();
  
  while (stack.length > 0) {
    const nextNode = stack.pop() as string;
    if (visited.has(nextNode)) {
      continue;
    }
    visited.add(nextNode);
    rv.push(nextNode);
    const preds = graph.predecessors(nextNode);
    for (const pred of preds) {
      if (!visited.has(pred)) {
        stack.push(pred);
      }
    }
  }
  return rv;
};

export class ReportBuilderState {
  protected measuresById: { [key: string]: ReportBuilderMeasure };
  protected dimensionsById: { [key: string]: ReportBuilderDimension };

  public allDimensions: ReportBuilderDimension[] = [];
  public allMeasures: ReportBuilderMeasure[] = [];
  public selectedDimensionIds: string[] = [];
  public selectedMeasureIds: string[] = [];
  protected graph: SimpleGraph;
  protected measureGraph: SimpleGraph;

  protected _availableDimensionIds: string[] = [];
  protected _availableMeasureIds: string[] = [];
  protected chartType?: string;

  constructor(
    graph: SimpleGraph,
    allDimensions: ReportBuilderDimension[],
    allMeasures: ReportBuilderMeasure[],
    initialSelectedDimensions: string[],
    initialSelectedMeasures: string[],
    chartType?: string,
  ) {
    this.allDimensions = allDimensions;
    this.allMeasures = allMeasures;
    this._availableDimensionIds = [];
    this._availableMeasureIds = [];
    this.selectedDimensionIds = initialSelectedDimensions;
    this.selectedMeasureIds = initialSelectedMeasures;
    this.graph = graph;
    this.chartType = chartType;

    const measuresById: { [key: string]: ReportBuilderMeasure } = {};
    this.allMeasures.forEach(m => {
      measuresById[m.id] = m;
    });
    this.measuresById = measuresById;

    const dimensionsById: { [key: string]: ReportBuilderDimension } = {};
    this.allDimensions.forEach(d => {
      dimensionsById[d.id] = d;
    });
    this.dimensionsById = dimensionsById;

    // Create a graph of measure dependencies for calculations
    this.measureGraph = new SimpleGraph();
    this.allMeasures.forEach(m => {
      this.measureGraph.setNode(m.id, m.name);
      if (m.is_calculation && m.parent_measure_ids) {
        m.parent_measure_ids.forEach(parentId => {
          this.measureGraph.setEdge(parentId, m.id);
        });
      }
    });

    this.updateAvailableDimensionIds();
    this.updateAvailableMeasureIds();
  }

  public get availableDimensionIds() {
    return this._availableDimensionIds;
  }

  public get availableMeasureIds() {
    return this._availableMeasureIds;
  }

  public hasMissingDeps(measure: ReportBuilderMeasure): boolean {
    if (!measure.is_calculation || !measure.parent_measure_ids) {
      return false;
    }

    return (
      this.selectedMeasureIds.includes(measure.id) &&
      measure.parent_measure_ids.some(
        parentId => !this.selectedMeasureIds.includes(parentId)
      )
    );
  }

  public getMissingDeps(measure: ReportBuilderMeasure): string[] {
    if (!measure.is_calculation || !measure.parent_measure_ids) {
      return [];
    }
    return measure.parent_measure_ids.filter(
      id => !this.selectedMeasureIds.includes(id)
    );
  }

  public getMeasureNameById(id: string): string {
    return this.measuresById[id]?.name || id;
  }

  public hasAnyMissingDeps(): boolean {
    return this.allMeasures.some(measure => this.hasMissingDeps(measure));
  }

  public getMissingDepsMessage(measure: ReportBuilderMeasure): string {
    const missingDeps = this.getMissingDeps(measure);
    if (!measure.parent_measure_ids || missingDeps.length === 0) {
      return '';
    }

    return `This measure requires ${measure.parent_measure_ids
      .map(id => this.getMeasureNameById(id))
      .join(' and ')} in order to be calculated, but is missing ${
      missingDeps.map(id => this.getMeasureNameById(id)).join(', ')
    }.`;
  }

  /**
   * Returns the pipeline node IDs that a single basic/cohort measure connects to
   * via its resolved dimension_join_trees (where join_tree or custom_path is set and not ignored).
   */
  protected getEffectiveNodeIds(measure: ReportBuilderMeasure): string[] {
    const nodeIds: string[] = [];
    if (measure.pipeline_node_id) {
      nodeIds.push(measure.pipeline_node_id);
    }
    const joinTreeNodeIds = measure.dimension_join_trees
      ?.filter(d => !d.ignore && d.join_tree != null)
      .map(d => d.pipeline_node_id) || [];
    nodeIds.push(...joinTreeNodeIds);
    return [...new Set(nodeIds)];
  }

  /**
   * Returns the pipeline node IDs that a measure of any type connects to.
   * - BASIC: delegates to getEffectiveNodeIds
   * - COHORT: resolves to activity measure, then getEffectiveNodeIds
   * - CALCULATED: finds all upstream basic/cohort measures, calls getEffectiveNodeIds on each, returns intersection
   */
  protected getResolvedNodeIds(measureId: string): string[] {
    const measure = this.measuresById[measureId];
    if (!measure) return [];

    // COHORT: resolve to activity measure
    if (measure.metric_type === 'COHORT' && measure.activity_measure_id) {
      const activityMeasure = this.measuresById[measure.activity_measure_id];
      if (activityMeasure) {
        return this.getEffectiveNodeIds(activityMeasure);
      }
      return [];
    }

    // CALCULATED: intersect upstream basics/cohorts
    if (measure.is_calculation) {
      const allUpstream = getAllUpstreamNodes(measure.id, this.measureGraph);
      const upstreamBasics = allUpstream
        .map(id => this.measuresById[id])
        .filter(m => m && !m.is_calculation);

      if (upstreamBasics.length === 0) return [];

      let result: string[] | null = null;
      for (const upstream of upstreamBasics) {
        let nodeIds: string[];
        if (upstream.metric_type === 'COHORT' && upstream.activity_measure_id) {
          const activityMeasure = this.measuresById[upstream.activity_measure_id];
          nodeIds = activityMeasure ? this.getEffectiveNodeIds(activityMeasure) : [];
        } else {
          nodeIds = this.getEffectiveNodeIds(upstream);
        }
        if (result === null) {
          result = nodeIds;
        } else {
          result = result.filter(id => nodeIds.includes(id));
        }
      }
      return result || [];
    }

    // BASIC: direct
    return this.getEffectiveNodeIds(measure);
  }

  public updateAvailableDimensionIds() {
    if (this.selectedMeasureIds.length === 0) {
      this._availableDimensionIds = [];
      return;
    }

    // Intersect resolved node IDs across all selected metrics
    let allowedNodeIds: string[] | null = null;
    for (const mid of this.selectedMeasureIds) {
      const nodeIds = this.getResolvedNodeIds(mid);
      if (allowedNodeIds === null) {
        allowedNodeIds = nodeIds;
      } else {
        allowedNodeIds = allowedNodeIds.filter(id => nodeIds.includes(id));
      }
    }

    const resolvedNodeIds = allowedNodeIds || [];

    // MAX_NODES guard
    const currentlySelectedNodeIds = new Set(
      this.allDimensions
        .filter(d => this.selectedDimensionIds.includes(d.id))
        .map(d => d.pipeline_node_id)
    );
    const MAX_NODES = 5;

    this._availableDimensionIds = this.allDimensions
      .filter(d => {
        if (!d.pipeline_node_id) return false;
        // Always include already-selected dimensions
        if (this.selectedDimensionIds.includes(d.id)) return true;
        if (!resolvedNodeIds.includes(d.pipeline_node_id)) return false;
        if (currentlySelectedNodeIds.size >= MAX_NODES) {
          return currentlySelectedNodeIds.has(d.pipeline_node_id);
        }
        return true;
      })
      .map(d => d.id);
  }

  protected updateAvailableMeasureIds() {
    // Get pipeline node IDs from selected dimensions
    const selectedDimNodeIds = this.selectedDimensionIds
      .filter(did => this.dimensionsById[did])
      .map(did => this.dimensionsById[did].pipeline_node_id);

    // If no dimensions selected, all metrics are available (except cohort for PIE)
    if (selectedDimNodeIds.length === 0) {
      this._availableMeasureIds = this.allMeasures
        .filter(m => !(this.chartType === 'PIE' && m.metric_type === 'COHORT'))
        .map(m => m.id);
      return;
    }

    const uniqueDimNodeIds = [...new Set(selectedDimNodeIds)];

    this._availableMeasureIds = this.allMeasures
      .filter(m => {
        // Cohort metrics require a date dimension, so exclude them from PIE charts
        if (this.chartType === 'PIE' && m.metric_type === 'COHORT') return false;

        // Always include already-selected metrics
        if (this.selectedMeasureIds.includes(m.id)) return true;

        // A metric is available if its resolved node IDs include ALL selected dimension node IDs
        const resolvedNodes = this.getResolvedNodeIds(m.id);
        return uniqueDimNodeIds.every(nodeId => resolvedNodes.includes(nodeId));
      })
      .map(m => m.id);
  }
}

export { SimpleGraph };

